import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill, inputCls, fmtMoney, fmtDate } from "@/components/Peep";

function CreatorPlanRow({ p }) {
  const status = p.creator_status !== "active" ? `creator ${p.creator_status}` : p.price === null ? "not set by creator" : "published";
  return (
    <div data-testid={`plan-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="min-w-0">
        <p className="font-semibold">{p.creator?.display_name || p.name}</p>
        <p className="text-xs text-[#a7a0af]">Last price update: {p.last_price_change ? fmtDate(p.last_price_change) : "never"}</p>
      </div>
      <div className="flex items-center gap-3">
        <span data-testid={`plan-price-readonly-${p.id}`} className="text-sm font-bold">{p.price === null ? "—" : `${fmtMoney(p.price)}/mo`}</span>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

function PlanRow({ p, onSaved }) {
  const [price, setPrice] = useState(p.price ?? "");
  const save = async (clear = false) => {
    try {
      await api.put(`/admin/plans/${p.id}`, clear ? { clear_price: true } : { price: Number(price) });
      toast.success(clear ? "Price cleared" : "Price saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid={`plan-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div>
        <p className="font-semibold">{p.name}</p>
        <p className="text-xs text-[#a7a0af]">{p.scope === "all_access" ? "Bundle · all creators" : `Creator · ${p.creator_status || ""}`} · {p.price === null ? "current: not set" : `current ${fmtMoney(p.price)}/mo`}</p>
      </div>
      <div className="flex items-center gap-2">
        {p.purchasable ? <StatusPill status="active" /> : <StatusPill status="not purchasable" />}
        <input type="number" min="0" step="0.01" inputMode="decimal" className={`${inputCls} w-28`} data-testid={`plan-price-${p.id}`} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="USD / mo" />
        <button onClick={() => save(false)} disabled={price === ""} data-testid={`plan-save-${p.id}`} className="btn-brand rounded-xl px-3 py-2 text-xs font-semibold">Save</button>
        {p.price !== null && <button onClick={() => save(true)} className="rounded-xl border border-white/15 px-3 py-2 text-xs">Clear</button>}
      </div>
    </div>
  );
}

function SettingsForm({ creators, s, onSaved }) {
  const [f, setF] = useState({
    processing_fee_pct: s.processing_fee_pct ?? "",
    processing_fee_fixed: s.processing_fee_fixed ?? "", grace_days: s.grace_days, payout_hold_days: s.payout_hold_days ?? 7,
    method: s.bundle_allocation?.method || "unset", weights: s.bundle_allocation?.weights || {},
  });
  const num = (k) => (f[k] === "" ? undefined : Number(f[k]));
  const save = async () => {
    const clear = ["processing_fee_pct", "processing_fee_fixed"].filter((k) => f[k] === "");
    const body = { processing_fee_pct: num("processing_fee_pct"), payout_hold_days: Number(f.payout_hold_days),
      processing_fee_fixed: num("processing_fee_fixed"), grace_days: Number(f.grace_days), clear,
      bundle_allocation: { method: f.method, weights: Object.fromEntries(Object.entries(f.weights).map(([k, v]) => [k, Number(v)])) } };
    try {
      await api.put("/admin/settings", body);
      toast.success("Revenue rules saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const field = (k, label, hint) => (
    <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">{label}
      <input type="number" min="0" step="0.01" inputMode="decimal" className={inputCls} data-testid={`setting-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={hint} />
    </label>
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 md:grid-cols-4">
        <div className="flex flex-col gap-1 text-xs text-[#a7a0af]">Revenue split (fixed)
          <p data-testid="setting-split" className={`${inputCls} text-white`}>Creator {s.creator_share_pct}% · PEEPING {s.platform_fee_pct}%</p>
        </div>
        {field("processing_fee_pct", "Processing fee % (estimate)", "Not set")}
        {field("processing_fee_fixed", "Processing fee fixed $", "Not set")}
        {field("grace_days", "Failed-payment grace days", "3")}
        {field("payout_hold_days", "Payout hold days", "7")}
      </div>
      <div>
        <p className="text-xs text-[#a7a0af]">All Access revenue allocation</p>
        <div className="mt-2 flex gap-2">
          {["unset", "equal", "weights"].map((m) => (
            <button key={m} onClick={() => setF({ ...f, method: m })} data-testid={`alloc-method-${m}`} className={`rounded-full border px-3 py-1.5 text-xs ${f.method === m ? "border-[#d42be8] bg-[#d42be8]/15" : "border-white/15"}`}>
              {{ unset: "Not decided (hold as unallocated)", equal: "Equal across active creators", weights: "Custom weights" }[m]}
            </button>
          ))}
        </div>
        {f.method === "weights" && (
          <div className="mt-3 grid gap-2 md:grid-cols-3">
            {creators.map((c) => (
              <label key={c.id} className="flex flex-col gap-1 text-xs text-[#a7a0af]">{c.display_name} %
                <input type="number" min="0" max="100" className={inputCls} data-testid={`alloc-weight-${c.handle}`} value={f.weights[c.id] ?? ""} onChange={(e) => setF({ ...f, weights: { ...f.weights, [c.id]: e.target.value } })} />
              </label>
            ))}
            <p className="text-xs text-[#a7a0af] md:col-span-3">Weights must total 100%. No split is assumed — you decide.</p>
          </div>
        )}
      </div>
      <button onClick={save} data-testid="settings-save" className="btn-brand self-start rounded-xl px-5 py-2.5 text-sm font-semibold">Save revenue rules</button>
    </div>
  );
}

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [creators, setCreators] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data.settings)).catch(() => {});
    api.get("/admin/creators").then((r) => setCreators(r.data.creators)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-pricing" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Pricing & revenue rules</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">You manage PEEPING ALL ACCESS and platform-wide rules. Every sale is split 60% creator / 40% PEEPING on the server.</p>
      </div>
      <Panel title="PEEPING ALL ACCESS">
        <div className="flex flex-col gap-2">
          {plans.filter((p) => p.scope === "all_access").map((p) => <PlanRow key={`${p.id}-${p.price}`} p={p} onSaved={load} />)}
        </div>
        <p className="mt-2 text-xs text-[#a7a0af]">Price changes apply to new members only; existing members keep the price they agreed to.</p>
      </Panel>
      <Panel title="Creator memberships (set by each creator)">
        <p data-testid="creator-pricing-readonly-note" className="mb-3 text-xs text-[#a7a0af]">Creators set and publish their own monthly price ($3–$500) in Creator Studio → Membership Pricing. This list is read-only.</p>
        <div className="flex flex-col gap-2">
          {plans.filter((p) => p.scope !== "all_access").map((p) => <CreatorPlanRow key={p.id} p={p} />)}
          {plans.filter((p) => p.scope !== "all_access").length === 0 && <p className="text-sm text-[#a7a0af]">No creator memberships yet.</p>}
        </div>
      </Panel>
      <Panel title="Fees, grace period & bundle allocation">
        {settings && <SettingsForm key={JSON.stringify(settings) + creators.length} creators={creators} s={settings} onSaved={load} />}
      </Panel>
    </div>
  );
}
